import React, { Component } from 'react';
import { observable, action, computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import moment from 'moment';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import FeatherIcon from 'feather-icons-react';
import { FormInput } from 'shards-react';

import './style.scss';
import PageTitle from '../../../components/common/PageTitle';
import ModalLoading from '../../../components/common/ModalLoading';

// 가중치 디폴트: 주문 60 / 리뷰 25 / 평점 15
const DEFAULT_WEIGHTS = { order: 60, review: 25, rating: 15 };

const WEIGHT_FIELDS = [
    { key: 'order',  label: '주문수',     icon: 'shopping-cart' },
    { key: 'review', label: '리뷰수',     icon: 'message-square' },
    { key: 'rating', label: '평균 별점',  icon: 'star' },
];

const DATE_PRESETS = [
    { key: '1w',     label: '최근 1주' },
    { key: '2w',     label: '최근 2주' },
    { key: '1m',     label: '최근 1개월' },
    { key: '3m',     label: '최근 3개월' },
    { key: 'custom', label: '직접 입력' },
];

@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))
@inject(({ booster }) => ({ getPopularProducts: booster.getPopularProducts }))
@observer
class PopularProducts extends Component {

    // basic
    @observable store = {};
    @observable teamUser = {};
    @observable isLogin = false;
    @observable isLoading = false;

    // 필터 (기간 변경 시에만 서버 재호출)
    @observable filters = {
        preset: '2w',
        startDate: moment().subtract(13, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
    };

    // 클라이언트 랭킹 파라미터 (변경 시 서버 호출 없이 즉시 재계산)
    @observable topN = 10;
    @observable weights = { ...DEFAULT_WEIGHTS };
    @observable sortBy = 'score'; // 'score' | 'orderCount' | 'reviewCount' | 'avgGoodsPt'
    @observable sortDesc = true;

    // 서버 원시 지표
    @observable rawItems = [];
    @observable summary = {};
    @observable isLoaded = false;
    @observable loadError = '';

    constructor(props) {
        super(props);
        this.init();
    }

    @action
    init = async () => {
        if (this.props.isLogin) {
            this.isLogin = this.props.isLogin;
            this.teamUser = this.props.me;
            this.store = this.props.me.store ? this.props.me.store : {};
            this.fetchData();
        }
    };

    // 서버 호출 — 기간 변경/최초 로드 시에만
    @action
    fetchData = () => {
        let _this = this;
        this.isLoading = true;
        this.loadError = '';

        const params = {
            store_id: this.teamUser.selStore_id,
            shopNo: this.teamUser.selShopNo || 1,
            startDate: this.filters.startDate,
            endDate: this.filters.endDate,
        };

        this.props.getPopularProducts(params, action(function (res) {
            _this.isLoading = false;
            _this.isLoaded = true;
            if (res && res.errors && res.errors.length > 0) {
                _this.loadError = res.errors[0].message || '데이터를 불러오지 못했습니다.';
                _this.rawItems = [];
                _this.summary = {};
                return;
            }
            const data = res && res.data ? res.data : {};
            _this.rawItems = data.items || [];
            _this.summary = data.summary || {};
        }));
    };

    @action
    setDatePreset = (preset) => {
        this.filters.preset = preset;
        const today = moment();
        if (preset === '1w') {
            this.filters.startDate = today.clone().subtract(6, 'days').format('YYYY-MM-DD');
            this.filters.endDate = today.format('YYYY-MM-DD');
        } else if (preset === '2w') {
            this.filters.startDate = today.clone().subtract(13, 'days').format('YYYY-MM-DD');
            this.filters.endDate = today.format('YYYY-MM-DD');
        } else if (preset === '1m') {
            this.filters.startDate = today.clone().subtract(1, 'months').add(1, 'days').format('YYYY-MM-DD');
            this.filters.endDate = today.format('YYYY-MM-DD');
        } else if (preset === '3m') {
            this.filters.startDate = today.clone().subtract(3, 'months').add(1, 'days').format('YYYY-MM-DD');
            this.filters.endDate = today.format('YYYY-MM-DD');
        }
        if (preset !== 'custom') {
            this.fetchData();
        }
    };

    // 직접 입력 기간 적용
    @action
    applyCustomDate = () => {
        if (!this.filters.startDate || !this.filters.endDate) {
            toast('시작일과 종료일을 모두 선택해 주세요.');
            return;
        }
        if (moment(this.filters.startDate).isAfter(moment(this.filters.endDate))) {
            toast('시작일이 종료일보다 늦을 수 없습니다.');
            return;
        }
        this.fetchData();
    };

    @action
    setTopN = (n) => {
        this.topN = n; // 서버 호출 없음 — rankedItems 가 즉시 재계산
    };

    @action
    setWeight = (key, value) => {
        this.weights[key] = parseInt(value, 10); // 서버 호출 없음
    };

    @action
    resetWeights = () => {
        this.weights = { ...DEFAULT_WEIGHTS };
    };

    @action
    toggleSort = (field) => {
        if (this.sortBy === field) {
            this.sortDesc = !this.sortDesc;
        } else {
            this.sortBy = field;
            this.sortDesc = true;
        }
    };

    // 정규화(지표/지표최대) + 가중합 → 종합점수(0~100)
    @computed
    get scoredItems() {
        const items = this.rawItems || [];
        if (items.length === 0) return [];

        const maxOrder = Math.max(...items.map(it => it.orderCount || 0), 0);
        const maxReview = Math.max(...items.map(it => it.reviewCount || 0), 0);
        const maxRating = Math.max(...items.map(it => it.avgGoodsPt || 0), 0);
        const wSum = this.weights.order + this.weights.review + this.weights.rating;

        return items.map(it => {
            const normOrder = maxOrder > 0 ? (it.orderCount || 0) / maxOrder : 0;
            const normReview = maxReview > 0 ? (it.reviewCount || 0) / maxReview : 0;
            const normRating = maxRating > 0 ? (it.avgGoodsPt || 0) / maxRating : 0;
            const score = wSum > 0
                ? ((this.weights.order * normOrder + this.weights.review * normReview + this.weights.rating * normRating) / wSum) * 100
                : 0;
            return { ...it, score: Math.round(score * 10) / 10 };
        });
    }

    // 정렬 + TopN 슬라이스
    @computed
    get rankedItems() {
        const sorted = [...this.scoredItems].sort((a, b) => {
            const av = a[this.sortBy] === null || a[this.sortBy] === undefined ? -1 : a[this.sortBy];
            const bv = b[this.sortBy] === null || b[this.sortBy] === undefined ? -1 : b[this.sortBy];
            return this.sortDesc ? bv - av : av - bv;
        });
        return sorted.slice(0, this.topN);
    }

    // 가중치 비중(%) 계산
    weightShare = (key) => {
        const wSum = this.weights.order + this.weights.review + this.weights.rating;
        if (wSum === 0) return 0;
        return Math.round((this.weights[key] / wSum) * 100);
    };

    // goodsPt(0~100) → 5점 별점 표기
    renderStars = (avgGoodsPt) => {
        if (avgGoodsPt === null || avgGoodsPt === undefined) {
            return <span className="pp-rating pp-rating-none">-</span>;
        }
        const star5 = Math.round((avgGoodsPt / 20) * 10) / 10;
        return (
            <span className="pp-rating">
                <FeatherIcon icon="star" size="13" className="pp-star-icon" aria-hidden="true" />
                {star5.toFixed(1)}
            </span>
        );
    };

    renderSortIcon = (field) => {
        if (this.sortBy !== field) {
            return <FeatherIcon icon="chevron-down" size="12" className="pp-sort-icon pp-sort-idle" aria-hidden="true" />;
        }
        return <FeatherIcon icon={this.sortDesc ? 'chevron-down' : 'chevron-up'} size="12" className="pp-sort-icon pp-sort-active" aria-hidden="true" />;
    };

    render() {
        const summary = this.summary || {};
        const ranked = this.rankedItems;
        const hasData = this.isLoaded && !this.loadError && (this.rawItems || []).length > 0;

        return (
            <div className="main-content popular-products-page">
                <PageTitle />

                {(this.isLogin && this.teamUser.emailId) ? (
                    <div className="pp-container">

                        {/* === 요약 카드 === */}
                        <div className="pp-summary-grid">
                            <div className="pp-summary-card">
                                <div className="pp-summary-label">
                                    <FeatherIcon icon="shopping-cart" size="14" aria-hidden="true" />
                                    <span>기간 내 주문</span>
                                </div>
                                <div className="pp-summary-value">
                                    {(summary.totalOrderCount || 0).toLocaleString()}<span className="pp-unit">건</span>
                                </div>
                            </div>
                            <div className="pp-summary-card">
                                <div className="pp-summary-label">
                                    <FeatherIcon icon="message-square" size="14" aria-hidden="true" />
                                    <span>기간 내 리뷰</span>
                                </div>
                                <div className="pp-summary-value">
                                    {(summary.totalReviewCount || 0).toLocaleString()}<span className="pp-unit">개</span>
                                </div>
                            </div>
                            <div className="pp-summary-card">
                                <div className="pp-summary-label">
                                    <FeatherIcon icon="star" size="14" aria-hidden="true" />
                                    <span>평균 별점</span>
                                </div>
                                <div className="pp-summary-value">
                                    {summary.avgRating ? (Math.round((summary.avgRating / 20) * 10) / 10).toFixed(1) : '-'}
                                    <span className="pp-unit">/ 5점</span>
                                </div>
                            </div>
                            <div className="pp-summary-card">
                                <div className="pp-summary-label">
                                    <FeatherIcon icon="package" size="14" aria-hidden="true" />
                                    <span>분석 상품</span>
                                </div>
                                <div className="pp-summary-value">
                                    {(summary.productCount || 0).toLocaleString()}<span className="pp-unit">개</span>
                                </div>
                            </div>
                        </div>

                        {/* === 필터 바: 기간 프리셋 + 커스텀 날짜 + TopN === */}
                        <div className="pp-toolbar">
                            <div className="pp-toolbar-row">
                                <div className="pp-toolbar-group">
                                    <span className="pp-toolbar-title">
                                        <FeatherIcon icon="calendar" size="13" aria-hidden="true" /> 분석 기간
                                    </span>
                                    <div className="pp-presets" role="radiogroup" aria-label="분석 기간 선택">
                                        {DATE_PRESETS.map(p => (
                                            <button key={p.key} type="button"
                                                    role="radio"
                                                    aria-checked={this.filters.preset === p.key}
                                                    className={`pp-preset ${this.filters.preset === p.key ? 'is-active' : ''}`}
                                                    onClick={() => this.setDatePreset(p.key)}>
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                    <span className="pp-period-text">
                                        {this.filters.startDate} ~ {this.filters.endDate}
                                    </span>
                                </div>

                                <div className="pp-toolbar-group">
                                    <span className="pp-toolbar-title">
                                        <FeatherIcon icon="bar-chart-2" size="13" aria-hidden="true" /> 표시 개수
                                    </span>
                                    <div className="pp-presets" role="radiogroup" aria-label="Top N 선택">
                                        {[10, 20, 30].map(n => (
                                            <button key={n} type="button"
                                                    role="radio"
                                                    aria-checked={this.topN === n}
                                                    className={`pp-preset ${this.topN === n ? 'is-active' : ''}`}
                                                    onClick={() => this.setTopN(n)}>
                                                TOP {n}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* 커스텀 날짜 입력 */}
                            {this.filters.preset === 'custom' && (
                                <div className="pp-toolbar-row pp-toolbar-custom">
                                    <FormInput type="date" size="sm"
                                               value={this.filters.startDate}
                                               onChange={(e) => this.filters.startDate = e.target.value}
                                               aria-label="시작일"
                                               style={{ maxWidth: '160px' }} />
                                    <span className="pp-date-sep">~</span>
                                    <FormInput type="date" size="sm"
                                               value={this.filters.endDate}
                                               onChange={(e) => this.filters.endDate = e.target.value}
                                               aria-label="종료일"
                                               style={{ maxWidth: '160px' }} />
                                    <button type="button" className="pp-apply-btn" onClick={() => this.applyCustomDate()}>
                                        적용
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* === 가중치 조절 패널 === */}
                        <div className="pp-weights">
                            <div className="pp-weights-header">
                                <div className="pp-weights-title">
                                    <FeatherIcon icon="sliders" size="15" aria-hidden="true" />
                                    <strong>랭킹 가중치</strong>
                                    <span className="pp-weights-desc">지표별 중요도를 조절하면 종합점수와 순위가 즉시 재계산됩니다</span>
                                </div>
                                <button type="button" className="pp-reset-btn"
                                        onClick={() => this.resetWeights()}
                                        title="기본값(주문 60 / 리뷰 25 / 평점 15)으로 되돌리기">
                                    <FeatherIcon icon="rotate-ccw" size="12" aria-hidden="true" /> 기본값
                                </button>
                            </div>
                            <div className="pp-weights-body">
                                {WEIGHT_FIELDS.map(f => (
                                    <div key={f.key} className="pp-weight-row">
                                        <span className="pp-weight-label">
                                            <FeatherIcon icon={f.icon} size="13" aria-hidden="true" />
                                            {f.label}
                                        </span>
                                        <input type="range" min="0" max="100" step="5"
                                               className="pp-weight-slider"
                                               value={this.weights[f.key]}
                                               onChange={(e) => this.setWeight(f.key, e.target.value)}
                                               aria-label={`${f.label} 가중치`}
                                               aria-valuemin={0}
                                               aria-valuemax={100}
                                               aria-valuenow={this.weights[f.key]} />
                                        <span className="pp-weight-value">{this.weights[f.key]}</span>
                                        <span className="pp-weight-share">비중 {this.weightShare(f.key)}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* === 랭킹 테이블 === */}
                        {hasData && (
                            <div className="pp-table-wrap">
                                <table className="pp-table">
                                    <colgroup>
                                        <col width="64" />
                                        <col />
                                        <col width="110" />
                                        <col width="110" />
                                        <col width="120" />
                                        <col width="170" />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th className="text-center">순위</th>
                                            <th>상품</th>
                                            <th className="pp-sortable text-right" onClick={() => this.toggleSort('orderCount')}>
                                                주문수 {this.renderSortIcon('orderCount')}
                                            </th>
                                            <th className="pp-sortable text-right" onClick={() => this.toggleSort('reviewCount')}>
                                                리뷰수 {this.renderSortIcon('reviewCount')}
                                            </th>
                                            <th className="pp-sortable text-right" onClick={() => this.toggleSort('avgGoodsPt')}>
                                                평균 별점 {this.renderSortIcon('avgGoodsPt')}
                                            </th>
                                            <th className="pp-sortable" onClick={() => this.toggleSort('score')}>
                                                종합점수 {this.renderSortIcon('score')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ranked.map((item, idx) => {
                                            const rank = idx + 1;
                                            return (
                                                <tr key={item.product_id} className={rank <= 3 ? `pp-row-top pp-row-top-${rank}` : ''}>
                                                    <td className="text-center">
                                                        <span className={`pp-rank ${rank <= 3 ? `pp-rank-${rank}` : ''}`}>{rank}</span>
                                                    </td>
                                                    <td>
                                                        <div className="pp-product">
                                                            {item.image ? (
                                                                <img className="pp-product-img" src={item.image} alt={item.productName} />
                                                            ) : (
                                                                <div className="pp-product-img pp-product-img-empty">
                                                                    <FeatherIcon icon="image" size="16" aria-hidden="true" />
                                                                </div>
                                                            )}
                                                            <div className="pp-product-info">
                                                                <div className="pp-product-name" title={item.productName}>{item.productName}</div>
                                                                <div className="pp-product-meta">
                                                                    {item.platformProductId && <span>상품번호 {item.platformProductId}</span>}
                                                                    {item.price ? <span>{item.price.toLocaleString()}원</span> : null}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="text-right"><strong>{item.orderCount.toLocaleString()}</strong></td>
                                                    <td className="text-right">{item.reviewCount.toLocaleString()}</td>
                                                    <td className="text-right">{this.renderStars(item.avgGoodsPt)}</td>
                                                    <td>
                                                        <div className="pp-score">
                                                            <div className="pp-score-bar">
                                                                <div className="pp-score-fill" style={{ width: `${Math.min(100, item.score)}%` }} />
                                                            </div>
                                                            <span className="pp-score-num">{item.score.toFixed(1)}</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* === 빈 상태 === */}
                        {this.isLoaded && !this.loadError && (this.rawItems || []).length === 0 && (
                            <div className="pp-empty">
                                <FeatherIcon icon="inbox" size="40" aria-hidden="true" />
                                <p className="pp-empty-title">선택한 기간에 주문 데이터가 없습니다</p>
                                <p className="pp-empty-desc">분석 기간을 늘려서 다시 확인해 보세요.</p>
                                <button type="button" className="pp-apply-btn" onClick={() => this.setDatePreset('3m')}>
                                    최근 3개월로 보기
                                </button>
                            </div>
                        )}

                        {/* === 에러 상태 === */}
                        {this.isLoaded && this.loadError && (
                            <div className="pp-empty pp-error">
                                <FeatherIcon icon="alert-triangle" size="40" aria-hidden="true" />
                                <p className="pp-empty-title">데이터를 불러오지 못했습니다</p>
                                <p className="pp-empty-desc">{this.loadError}</p>
                                <button type="button" className="pp-apply-btn" onClick={() => this.fetchData()}>
                                    다시 시도
                                </button>
                            </div>
                        )}

                    </div>
                ) : null}

                <ModalLoading isLoading={this.isLoading} />
            </div>
        );
    }
}

export default PopularProducts;
